"use client";

import { useEffect } from "react";

import { useSessionContext } from "@/components/session/SessionProvider";
import { useOnboardingStateQuery } from "@/hooks/useOnboarding";
import { setAnalyticsContext, track } from "@/lib/analytics/track";
import { useNamespaceStore } from "@/state/namespace-store";

/**
 * Usage-analytics entry point for components: keeps the module-level tracker's
 * context (consent + active namespace) in sync with React state and returns
 * the `track` function. Safe to call from many components at once — the
 * onboarding query is deduped by React Query and the context sync is
 * idempotent.
 *
 * Consent wiring: `usageConsentAccepted` from the onboarding state is the
 * single source of truth. Until the query resolves the tracker buffers a small
 * number of events; an unauthenticated session or a declined consent hard
 * no-ops locally (the server enforces the same gate).
 */
export function useTrack(): typeof track {
  const { session } = useSessionContext();
  const authenticated = session.status === "authenticated";
  const namespaceId = useNamespaceStore((state) => state.selectedNamespaceId);

  const onboardingState = useOnboardingStateQuery({ enabled: authenticated });
  const usageConsentAccepted = onboardingState.data?.usageConsentAccepted;

  useEffect(() => {
    setAnalyticsContext({
      // Unauthenticated → definitive no; otherwise null while the query is
      // still loading/errored, resolving to the accepted flag once known.
      consentGranted: !authenticated ? false : (usageConsentAccepted ?? null),
      namespaceId,
    });
  }, [authenticated, usageConsentAccepted, namespaceId]);

  return track;
}
